import { Link } from 'react-router-dom';
import { Calendar, Users, Shield, Zap, Heart, Github, Mail, Briefcase, ArrowLeft, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const APropos = () => {
  const { t } = useTranslation();
  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 sm:p-12">
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <Star className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('about.title')}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('about.subtitle')}
          </p>
        </div>

        <div className="space-y-12">
          {/* Introduction */}
          <section className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                <Heart className="w-4 h-4 text-white" />
              </div>
              {t('about.mission_title')}
            </h2>
            <div className="space-y-4 text-gray-700">
              <p className="text-lg leading-relaxed">
                EasyRDV est une plateforme de prise de rendez-vous moderne et intuitive, conçue pour simplifier
                la gestion des rendez-vous pour les professionnels et les particuliers. Notre objectif est de
                rendre la prise de rendez-vous plus efficace, plus accessible et plus agréable pour tous.
              </p>
              <p className="leading-relaxed">
                Développée avec passion par Yassine Hamdoune, cette application combine simplicité d'utilisation
                et fonctionnalités avancées pour répondre aux besoins de tous les types d'utilisateurs.
              </p>
            </div>
          </section>

          {/* Fonctionnalités principales */}
          <section>
            <h2 className="text-3xl font-bold text-gray-800 mb-8 flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-3">
                <Zap className="w-4 h-4 text-white" />
              </div>
              {t('about.features_title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">{t('about.feature_calendar')}</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Interface intuitive pour gérer vos disponibilités et planifier vos rendez-vous en toute simplicité.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">{t('about.feature_users')}</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Système d'authentification sécurisé avec gestion des profils utilisateurs personnalisés.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">{t('about.feature_security')}</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Protection des données personnelles conformément au RGPD avec chiffrement et sauvegardes sécurisées.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-3">{t('about.feature_performance')}</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Application rapide et réactive, optimisée pour une expérience utilisateur fluide sur tous les appareils.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Le développeur */}
          <section className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-3">
                <Briefcase className="w-4 h-4 text-white" />
              </div>
              {t('about.developer_title')}
            </h2>
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                  <img src="./1.png" alt="" /> 
                </div>
                <div className="text-center sm:text-left">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Yassine Hamdoune</h3>
                  <p className="text-lg text-gray-600 mb-2">{t('about.developer_role')}</p>
                  <div className="flex flex-wrap justify-center sm:justify-start gap-3">
                    <a
                      href="https://github.com/yacinova"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-gray-600 hover:text-indigo-600 transition-colors px-3 py-1 bg-gray-100 rounded-full hover:bg-indigo-100"
                    >
                      <Github className="w-4 h-4" />
                      <span>GitHub</span>
                    </a>
                    <a
                      href="mailto:hamdouneyassine.03@gmail.com"
                      className="flex items-center space-x-2 text-gray-600 hover:text-indigo-600 transition-colors px-3 py-1 bg-gray-100 rounded-full hover:bg-indigo-100"
                    >
                      <Mail className="w-4 h-4" />
                      <span>Email</span>
                    </a>
                    <a
                      href="https://yassinehamdoune.vercel.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-gray-600 hover:text-indigo-600 transition-colors px-3 py-1 bg-gray-100 rounded-full hover:bg-indigo-100"
                    >
                      <Briefcase className="w-4 h-4" />
                      <span>Portfolio</span>
                    </a>
                  </div>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed text-center sm:text-left">
                Passionné par le développement web et les nouvelles technologies, je me consacre à créer
                des applications web modernes et performantes. EasyRDV est le fruit de cette passion,
                combinant expertise technique et vision utilisateur.
              </p>
            </div>
          </section>

          {/* Valeurs */}
          <section>
            <h2 className="text-3xl font-bold text-gray-800 mb-8 flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-3">
                <Heart className="w-4 h-4 text-white" />
              </div>
              {t('about.values_title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{t('about.value_passion')}</h3>
                <p className="text-gray-600 leading-relaxed">
                  Nous développons avec passion pour créer des solutions qui améliorent la vie quotidienne.
                </p>
              </div>

              <div className="text-center bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{t('about.value_security')}</h3>
                <p className="text-gray-600 leading-relaxed">
                  La protection de vos données et de votre vie privée est notre priorité absolue.
                </p>
              </div>

              <div className="text-center bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{t('about.value_innovation')}</h3>
                <p className="text-gray-600 leading-relaxed">
                  Nous cherchons constamment à améliorer et innover pour offrir la meilleure expérience.
                </p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-3">
                <Mail className="w-4 h-4 text-white" />
              </div>
              {t('about.contact_title')}
            </h2>
            <div className="space-y-4 text-gray-700">
              <p className="text-lg">Pour toute question, suggestion ou collaboration :</p>
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-indigo-500" />
                    <span><strong>{t('about.contact_email_label')}:</strong> hamdouneyassine.03@gmail.com</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Github className="w-5 h-5 text-indigo-500" />
                    <span><strong>{t('about.contact_github_label')}:</strong> <a href="https://github.com/yacinova" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 underline">{t('about.contact_github_link')}</a></span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Briefcase className="w-5 h-5 text-indigo-500" />
                    <span><strong>{t('about.contact_developer_label')}:</strong> Yassine Hamdoune</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Version */}
          <section className="bg-gradient-to-br from-gray-50 to-indigo-50 p-6 rounded-2xl">
            <div className="text-center">
              <p className="text-sm text-gray-600">
                <strong>{t('about.version_label')}:</strong> 1.0.0
              </p>
              <p className="text-sm text-gray-600 mt-2">
                <strong>{t('about.version_last_update_label')}:</strong> 05/07/2025
              </p>
            </div>
          </section>
        </div>

        {/* Navigation */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <Link
              to="/"
              className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-semibold transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('about.back_to_home')}
            </Link>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link 
                to="/legal/mentions-legales" 
                className="text-gray-600 hover:text-indigo-600 transition-colors px-3 py-1 bg-gray-100 rounded-full hover:bg-indigo-100"
                onClick={() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }}
              >
                {t('about.legal_notice')}
              </Link>
              <Link 
                to="/legal/politique-confidentialite" 
                className="text-gray-600 hover:text-indigo-600 transition-colors px-3 py-1 bg-gray-100 rounded-full hover:bg-indigo-100"
                onClick={() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }}
              >
                {t('about.privacy_policy')}
              </Link>
              <Link 
                to="/legal/conditions-utilisation" 
                className="text-gray-600 hover:text-indigo-600 transition-colors px-3 py-1 bg-gray-100 rounded-full hover:bg-indigo-100"
                onClick={() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }}
              >
                {t('about.terms_of_use')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APropos; 